import type {
  AttendanceCounts,
  AttendanceMark,
  AttendanceRegister,
  AttendanceRow,
} from '@/types/api';

/**
 * The register, with marks applied — what `PUT /api/attendance` is about to
 * answer, worked out in the browser so the screen can show it now.
 *
 * This is a deliberate second implementation of two small rules that live in
 * `apps/api/src/routes/attendance.ts`: `markColumns` (the times are normalised
 * against the mark) and `summarize` (the five tiles). An optimistic update is
 * only worth having if it predicts the server exactly — a prediction that
 * differs is a value that visibly changes under the teacher a moment after they
 * entered it, which is worse than the round trip it saved. Both rules are
 * arithmetic over a roster this response cannot change, so the prediction is
 * exact rather than a guess; the response still lands and still wins.
 *
 * Keep the two in step. `attendanceRegister.test.ts` and the route's own tests
 * assert the same normalisation from either side.
 */
export function applyMarks(
  register: AttendanceRegister,
  marks: AttendanceMark[]
): AttendanceRegister {
  const markByStudent = new Map(marks.map((mark) => [mark.studentId, mark]));

  // Mapped over the roster rather than over the marks: a mark naming somebody
  // this register does not have is dropped, not appended. The server answers
  // 400 for a stranger and the mutation rolls back, so inventing a row here
  // would put a child on screen who was never in the room.
  const rows = register.rows.map((row) => {
    const mark = markByStudent.get(row.studentId);
    return mark ? applyMark(row, mark) : row;
  });

  return { ...register, rows, counts: summarize(rows) };
}

/**
 * One row, rewritten by one mark.
 *
 * Every field the mark carries is written, including the ones it leaves out:
 * the PUT is an upsert of the whole row rather than a patch, so an absent
 * `note` reaches the database as an explicit null. Rendering the old note until
 * the response lands would show a note already on its way to being deleted.
 *
 * `source` becomes `teacher` because this screen is the teacher: the front-desk
 * flag on a row belongs to the mark being replaced, not to the replacement.
 */
function applyMark(row: AttendanceRow, mark: AttendanceMark): AttendanceRow {
  return {
    ...row,
    status: mark.status,
    // An arrival time is what makes a tardy a tardy; on any other mark the
    // server drops it, so correcting a tardy to a present clears the 09:15
    // rather than leaving the row saying two things at once. An absent child
    // has neither time.
    arrivedAt: mark.status === 'tardy' ? (mark.arrivedAt ?? null) : null,
    leftAt: mark.status === 'absent' ? null : (mark.leftAt ?? null),
    note: normalizeNote(mark.note),
    source: 'teacher',
  };
}

/** The API's `z.string().trim()`, applied to the copy the screen keeps. */
function normalizeNote(note: string | null | undefined): string | null {
  const trimmed = note?.trim();
  return trimmed ? trimmed : null;
}

/**
 * The five tiles, counted the way the route's `summarize` counts them.
 *
 * `leftEarly` counts a departure *time*, not a status — a child who went home
 * at one o'clock was still present, and is counted in both. `notMarked` counts
 * rows nobody has answered for, which is what tells a teacher they are not
 * finished.
 */
function summarize(rows: AttendanceRow[]): AttendanceCounts {
  return {
    present: rows.filter((row) => row.status === 'present').length,
    absent: rows.filter((row) => row.status === 'absent').length,
    tardy: rows.filter((row) => row.status === 'tardy').length,
    leftEarly: rows.filter((row) => row.leftAt !== null).length,
    notMarked: rows.filter((row) => row.status === null).length,
  };
}
